import { useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useFeatures } from 'lib/featureFlags/features';
import { useGetTenantsByOrgAndProductCode } from '../services/inventory/tenants';
import { fetchLastSuccessfulLoginOrganizationId } from '../auth/auth0Storage';
import { useAuth0 } from '@auth0/auth0-react';
import { useMainStateDispatch } from 'features/main/context/main-context';
import { openDrawer } from 'features/main/context/drawer/actionCreators';

export interface NavigationMessage {
  type:
    | 'NAVIGATE'
    | 'ROUTE_CHANGE'
    | 'NAVIGATE_TO_DEVELOPER'
    | 'NAVIGATE_TO_MARKETPLACE'
    | 'NAVIGATE_TO_MARKETPLACE_APP_IDEAS'
    | 'NAVIGATE_TO_MARKETPLACE_APP_IDEA_DETAILS'
    | 'NAVIGATE_TO_MARKETPLACE_DETAILS'
    | 'NAVIGATE_TO_MARKETPLACE_PROFILE'
    | 'NAVIGATE_TO_MY_APPS'
    | 'GET_FEATURE_FLAGS'
    | 'FEATURE_FLAGS_RESPONSE'
    | 'AUTH_LOGIN_REQUIRED'
    | 'GET_AUTH_CONTEXT'
    | 'AUTH_CONTEXT_RESPONSE'
    | 'GET_TENANTS_BY_ORG_AND_PRODUCTCODE'
    | 'TENANTS_BY_ORG_AND_PRODUCTCODE_RESPONSE'
    | 'TOGGLE_HELP_DRAWER';
  payload: {
    path?: string;
    search?: string;
    state?: any;
    appId?: string;
    ideaId?: string;
    developerId?: string;
    appType?: 'custom' | 'public';
    status?: string;
    replace?: boolean;
    features?: Record<string, boolean>;
    env?: string;
    tenantsData?: any;
    returnTo?: string;
    userId?: string;
    page?: number;
    edit?: boolean;
    sortBy?: string;
  };
}

export interface IframeNavigationSyncOptions {
  allowedOrigins: string[];
  iframeRef?: React.RefObject<HTMLIFrameElement>;
  onNavigationMessage?: (message: NavigationMessage) => void;
  debugMode?: boolean;
}

export const useIframeNavigationSync = (
  options: IframeNavigationSyncOptions,
) => {
  const {
    allowedOrigins,
    iframeRef,
    onNavigationMessage,
    debugMode = false,
  } = options;
  const features = useFeatures();
  const organizationId = fetchLastSuccessfulLoginOrganizationId() ?? '';
  const { data: tenantsDataByOrgAndProductCode } =
    useGetTenantsByOrgAndProductCode({
      organizationId,
      productCode: 'mkpdevelopers',
    });
  const { loginWithRedirect, user } = useAuth0();
  const [, dispatch] = useMainStateDispatch();
  const history = useHistory();
  const location = useLocation();

  const log = useCallback(
    (message: string, data?: any) => {
      if (debugMode) {
        if (data !== undefined) {
          console.log(`[IframeNavigationSync] ${message}`, data);
        } else {
          console.log(`[IframeNavigationSync] ${message}`);
        }
      }
    },
    [debugMode],
  );

  // Handle incoming navigation messages from iframe
  const handleNavigationMessage = useCallback(
    (event: MessageEvent<NavigationMessage>) => {
      // Validate origin
      const isValidOrigin = allowedOrigins.some(
        (origin) => origin === '*' || event.origin === origin,
      );

      if (!isValidOrigin) {
        log('Rejected message from invalid origin', event.origin);
        return;
      }

      const { type, payload } = event.data;
      log('Received navigation message', { type, payload });

      // Call custom handler if provided
      if (onNavigationMessage) {
        onNavigationMessage(event.data);
      }

      // Handle built-in navigation types
      switch (type) {
        case 'NAVIGATE':
          if (payload.path) {
            const url = payload.search
              ? `${payload.path}?${payload.search}`
              : payload.path;
            if (payload.replace) {
              history.replace(url, payload.state);
            } else {
              history.push(url, payload.state);
            }
          }
          break;

        case 'NAVIGATE_TO_DEVELOPER': {
          const searchParams = new URLSearchParams(location.search);

          let basePath = '/app-studio';
          if (payload.appType === 'public' && payload.appId) {
            basePath = '/app-studio/public-app';
          } else if (payload.appId) {
            basePath = '/app-studio/custom-app';
          }
          if (payload.appId) {
            searchParams.set('appId', payload.appId);
          } else {
            searchParams.delete('appId');
          }
          // Status pre-selects the App studio list filter; drop it so an old filter doesn't stick
          if (payload.status && !payload.appId) {
            searchParams.set('status', payload.status);
          } else {
            searchParams.delete('status');
          }
          const developerUrl = `${basePath}${
            searchParams.toString() ? `?${searchParams.toString()}` : ''
          }`;
          history.push(developerUrl);
          break;
        }

        case 'NAVIGATE_TO_MARKETPLACE': {
          history.push(`/marketplace${location.search}`);
          break;
        }

        case 'NAVIGATE_TO_MARKETPLACE_APP_IDEAS': {
          const searchParams = new URLSearchParams(location.search);
          if (payload.page && payload.page > 1) {
            searchParams.set('page', String(payload.page));
          } else {
            searchParams.delete('page');
          }
          if (payload.search) {
            searchParams.set('search', String(payload.search));
          } else {
            searchParams.delete('search');
          }
          if (payload.sortBy) {
            searchParams.set('sortBy', payload.sortBy);
          } else {
            searchParams.delete('sortBy');
          }
          const url = `/marketplace/app-ideas${
            searchParams.toString() ? `?${searchParams.toString()}` : ''
          }`;
          history.push(url);
          break;
        }

        case 'NAVIGATE_TO_MARKETPLACE_APP_IDEA_DETAILS': {
          const ideaId = payload.ideaId ?? payload.appId;
          if (ideaId) {
            const searchParams = new URLSearchParams(location.search);
            if (payload.page && payload.page > 1) {
              searchParams.set('page', String(payload.page));
            } else {
              searchParams.delete('page');
            }
            if (payload.search) {
              searchParams.set('search', String(payload.search));
            } else {
              searchParams.delete('search');
            }
            if (payload.edit) {
              searchParams.set('edit', 'true');
            } else {
              searchParams.delete('edit');
            }
            if (payload.sortBy) {
              searchParams.set('sortBy', payload.sortBy);
            } else {
              searchParams.delete('sortBy');
            }
            const url = `/marketplace/app-ideas/${ideaId}${
              searchParams.toString() ? `?${searchParams.toString()}` : ''
            }`;
            history.push(url);
          }
          break;
        }

        case 'NAVIGATE_TO_MARKETPLACE_DETAILS': {
          const searchParams = new URLSearchParams(location.search);
          if (payload.appId) {
            searchParams.set('id', payload.appId);
          }
          const url = `/marketplace/details${
            searchParams.toString() ? `?${searchParams.toString()}` : ''
          }`;
          history.push(url);
          break;
        }

        case 'NAVIGATE_TO_MARKETPLACE_PROFILE': {
          const searchParams = new URLSearchParams(location.search);
          if (payload.developerId) {
            searchParams.set('id', payload.developerId);
          }
          const url = `/marketplace/profile${
            searchParams.toString() ? `?${searchParams.toString()}` : ''
          }`;
          history.push(url);
          break;
        }

        case 'NAVIGATE_TO_MY_APPS': {
          history.push(`/my-apps${location.search}`);
          break;
        }

        case 'AUTH_LOGIN_REQUIRED': {
          // Only same-site relative paths, to avoid open redirects after login.
          const requestedReturnTo = payload?.returnTo;
          const isSafeReturnTo =
            typeof requestedReturnTo === 'string' &&
            requestedReturnTo.startsWith('/') &&
            !requestedReturnTo.startsWith('//') &&
            !requestedReturnTo.startsWith('/\\');
          loginWithRedirect({
            appState: {
              returnTo: isSafeReturnTo
                ? requestedReturnTo
                : window.location.pathname + window.location.search,
            },
          });
          break;
        }

        case 'GET_AUTH_CONTEXT': {
          // Lets the iframe drop cached tokens that belong to a different user.
          if (
            event.source &&
            event.source === iframeRef?.current?.contentWindow
          ) {
            const response: NavigationMessage = {
              type: 'AUTH_CONTEXT_RESPONSE',
              payload: { userId: user?.sub },
            };
            (event.source as WindowProxy).postMessage(response, event.origin);
            log('Sent auth context response');
          }
          break;
        }

        case 'GET_FEATURE_FLAGS': {
          if (event.source && 'postMessage' in event.source) {
            const response: NavigationMessage = {
              type: 'FEATURE_FLAGS_RESPONSE',
              payload: {
                features,
              },
            };
            (event.source as WindowProxy).postMessage(response, event.origin);
            log('Sent feature flags response', response);
          }
          break;
        }

        case 'TOGGLE_HELP_DRAWER': {
          dispatch(openDrawer('help-drawer'));
          break;
        }

        case 'GET_TENANTS_BY_ORG_AND_PRODUCTCODE': {
          if (
            event.source &&
            'postMessage' in event.source &&
            tenantsDataByOrgAndProductCode
          ) {
            const response = {
              type: 'TENANTS_BY_ORG_AND_PRODUCTCODE_RESPONSE' as const,
              payload: tenantsDataByOrgAndProductCode ?? [],
            };
            (event.source as WindowProxy).postMessage(response, event.origin);
            log('Sent tenants by org and product code response', response);
          }
          break;
        }

        default:
          log('Unknown navigation message type', type);
      }
    },
    [
      allowedOrigins,
      iframeRef,
      log,
      onNavigationMessage,
      history,
      location.search,
      loginWithRedirect,
      user,
      dispatch,
      features,
      tenantsDataByOrgAndProductCode,
    ],
  );

  // Set up message listener
  useEffect(() => {
    window.addEventListener('message', handleNavigationMessage);
    log('Navigation sync initialized', { allowedOrigins });

    return () => {
      window.removeEventListener('message', handleNavigationMessage);
    };
  }, [handleNavigationMessage, allowedOrigins, log]);

  return {
    currentRoute: `${location.pathname}${location.search}`,
  };
};
